/**
 * SalesRepsTab -- shown as the event's "Confirm Schedule" tab (GS-05, MSR requirements 09-30: "Rename the team
 * section to 'Confirm schedule'. For each date, select one or more reps working"). The list is still the event's
 * Sales Reps list (server messages call it that); below it, a dates x reps grid for multi-day date-model events.
 */

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEventSalesAssignments,
  getEventSalesAssignmentDays,
  deleteEventSalesAssignment,
} from "../../../services/salesCalendar.service";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { useToast } from "../../ui/Toast";
import { formatRepDays } from "../../../utils/msrEventFormat";
import type { AssignmentRole } from "../../../types/salesCalendar.types";
import { ASSIGNMENT_ROLE_LABELS } from "../../../types/salesCalendar.types";
import { POSITION_TYPE_LABELS } from "../../../types/sales.types";
import AssignRepModal from "../../salesCalendar/AssignRepModal";
import ConfirmScheduleGrid from "../ConfirmScheduleGrid";
import Button from "../../common/Button";
import Badge from "../../ui/Badge";
import {
  PlusIcon,
  TrashIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import type { UUID } from "../../../types/common.types";
import { useAuth } from "../../../contexts/AuthContext";
import { canAssignEventSalesReps } from "../../../utils/salesRepAssignPermission";
import { useSchedulingDelegations } from "../../../hooks/useSchedulingDelegations";
import { useEventTimeOffConflicts } from "../../../hooks/useTimeOff";
import { joinDays } from "../../../services/timeOffConflicts";

interface SalesRepsTabProps {
  eventId: UUID;
  eventTitle: string;
}

const SalesRepsTab: React.FC<SalesRepsTabProps> = ({ eventId, eventTitle }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // The list is readable by everyone; only these roles may change it (database rule, migration 061).
  // mig 1780: a scheduling delegate too (the database admits only reps of the team they schedule for, on its events)
  const { isDelegate } = useSchedulingDelegations();
  const canAssign = canAssignEventSalesReps(user?.role) || isDelegate;
  const [showAssignModal, setShowAssignModal] = useState(false);
  const { showToast } = useToast();
  // Removing a rep asks first (it changes who may run the event and who sees it on their MSR schedule).
  const [pendingRemoval, setPendingRemoval] = useState<{
    id: UUID;
    name: string;
  } | null>(null);

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ["event-sales-assignments", eventId],
    queryFn: () => getEventSalesAssignments(eventId),
  });

  // The days a per-day rep works (only read when someone is per-day).
  const hasPerDay = assignments.some((a) => a.day_scope === "selected");
  const { data: repDays = {} } = useQuery({
    queryKey: ["event-sales-assignment-days", eventId],
    queryFn: () => getEventSalesAssignmentDays(eventId),
    enabled: hasPerDay,
  });

  // MSR QA 10-20: a rep with time off on a day they work here gets a "Time off" badge (warning only).
  const { conflicts: timeOffConflicts } = useEventTimeOffConflicts(eventId);
  const timeOffOf = (positionId: string) =>
    timeOffConflicts.find((c) => c.positionId === positionId)?.dates ?? [];

  const removeMutation = useMutation({
    mutationFn: (id: UUID) => deleteEventSalesAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignments", eventId],
      });
      queryClient.invalidateQueries({
        queryKey: ["event-sales-assignment-days", eventId],
      });
      queryClient.invalidateQueries({ queryKey: ["sales-calendar"] });
      // GS-05: the Confirm schedule grid reads the staffing set + version
      queryClient.invalidateQueries({ queryKey: ["event-staffing", eventId] });
      // Migration 417: the list's lead is the event's Lead/Event Rep, so the event header may have changed too.
      queryClient.invalidateQueries({
        queryKey: ["events", "detail", eventId],
      });
      showToast("success", "Rep removed from this event");
    },
    onError: (err: unknown) => {
      showToast(
        "error",
        (err as { message?: string } | null)?.message ??
          "The rep could not be removed.",
      );
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-900">
          Confirm schedule
        </h2>
        <p className="text-xs text-gray-500">
          The event's Sales Reps list: who works this event, and on which days.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">
          Assigned Sales Reps ({assignments.length})
        </h3>
        {canAssign && (
          <Button size="sm" onClick={() => setShowAssignModal(true)}>
            <PlusIcon className="h-4 w-4 mr-1" />
            Assign Rep
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 text-center py-6">Loading...</div>
      ) : assignments.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-8 bg-gray-50 rounded-lg">
          No sales reps assigned to this event.
          {canAssign && (
            <>
              <br />
              <button
                onClick={() => setShowAssignModal(true)}
                className="text-blue-600 hover:text-blue-800 mt-1"
              >
                Assign a rep
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {assignments.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <UserCircleIcon className="h-8 w-8 text-gray-300" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {a.sales_position?.assigned_name || "Unassigned"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {a.sales_position?.title}
                    {a.sales_position?.position_type &&
                      ` · ${POSITION_TYPE_LABELS[a.sales_position.position_type as keyof typeof POSITION_TYPE_LABELS] || a.sales_position.position_type}`}
                  </div>
                  <div className="text-xs text-gray-600">
                    {a.day_scope === "selected"
                      ? repDays[a.id]?.length
                        ? `Works ${formatRepDays(repDays[a.id])}`
                        : "Works some days"
                      : "Works every day"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {timeOffOf(a.sales_position_id).length > 0 && (
                  <span
                    className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900"
                    data-testid="time-off-badge"
                    title={`Not marked available: ${joinDays(timeOffOf(a.sales_position_id))}`}
                  >
                    Not available {joinDays(timeOffOf(a.sales_position_id))}
                  </span>
                )}
                <Badge color={a.role === "lead" ? "blue" : "gray"} size="sm">
                  {ASSIGNMENT_ROLE_LABELS[a.role as AssignmentRole] || a.role}
                </Badge>
                {canAssign && (
                  <button
                    onClick={() =>
                      setPendingRemoval({
                        id: a.id,
                        name:
                          a.sales_position?.assigned_name ||
                          a.sales_position?.title ||
                          "this rep",
                      })
                    }
                    className="text-gray-400 hover:text-red-600"
                    title="Remove assignment"
                    aria-label={`Remove ${a.sales_position?.assigned_name || "rep"}`}
                    disabled={removeMutation.isPending}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {assignments.length > 0 && (
        <ConfirmScheduleGrid
          eventId={eventId}
          canEdit={canAssign}
          names={Object.fromEntries(
            assignments.map((a) => [
              a.sales_position_id,
              a.sales_position?.assigned_name ||
                a.sales_position?.title ||
                "Unassigned",
            ]),
          )}
        />
      )}

      <ConfirmDialog
        isOpen={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) {
            removeMutation.mutate(pendingRemoval.id);
          }
        }}
        title="Remove rep from this event?"
        message={`${pendingRemoval?.name ?? "This rep"} will be taken off this event's Sales Reps list.`}
        confirmText="Remove"
        variant="danger"
      />

      {canAssign && showAssignModal && (
        <AssignRepModal
          eventId={eventId}
          eventTitle={eventTitle}
          onClose={() => setShowAssignModal(false)}
        />
      )}
    </div>
  );
};

export default SalesRepsTab;
